import { createEntityAdapter } from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice";
import { format, parseISO } from "date-fns";

const orderAdapter = createEntityAdapter({
  selectId: (order) => order.orderId,
  sortComparer: (a, b) => b.orderId - a.orderId,
});

const initialState = orderAdapter.getInitialState();

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllOrders: builder.query({
      query: (userId) => `/Order`,
      transformResponse: (responseData) => {
        const loadedOrders = responseData.map((order) => {
          if (order?.orderDate)
            order.orderDate = format(parseISO(order.orderDate), "d MMMM yyyy");
          if (order.hasOwnProperty("salesPersonId")) {
            order.employeeId = order.salesPersonId;
            delete order.salesPersonId;
          }
          return order;
        });

        return orderAdapter.setAll(initialState, loadedOrders);
      },
      providesTags: (result, error, arg) => [
        { type: "Order", id: "LIST" },
        ...result.ids.map((id) => ({ type: "Order", id })),
      ],
    }),
    getOrdersByUserId: builder.query({
      query: (userId) => `/Order/ByUser/${userId}`,
      transformResponse: (responseData) => {
        const loadedOrders = responseData.map((order) => {
          if (order?.orderDate)
            order.orderDate = format(parseISO(order.orderDate), "d MMMM yyyy");
          if (order.hasOwnProperty("salesPersonId")) {
            order.employeeId = order.salesPersonId;
            delete order.salesPersonId;
          }
          return order;
        });
        return orderAdapter.setAll(initialState, loadedOrders);
      },
      providesTags: (result, error, arg) => [
        { type: "Order", id: "LIST" },
        ...result.ids.map((id) => ({ type: "Order", id })),
      ],
    }),
    getOrdersByType: builder.query({
      query: ({ type, startDate, endDate }) =>
        `/Order/ByType/${type}/${startDate}/${endDate}`,
      transformResponse: (responseData) => {
        const loadedOrders = responseData.map((order) => {
          if (order?.orderDate)
            order.orderDate = format(parseISO(order.orderDate), "d MMMM yyyy");
          if (order.hasOwnProperty("salesPersonId")) {
            order.employeeId = order.salesPersonId;
            delete order.salesPersonId;
          }
          return order;
        });
        return orderAdapter.setAll(initialState, loadedOrders);
      },
      providesTags: (result, error, arg) => [
        { type: "Order", id: "LIST" },
        ...result.ids.map((id) => ({ type: "Order", id })),
      ],
    }),
    getOrdersSummeryByUserId: builder.query({
      query: ({ userId, type, startDate, endDate }) =>
        `/Order/orders_summary/${userId}/${type}/${startDate}/${endDate}`,
      transformResponse: (responseData) => responseData,
      providesTags: (result, error, arg) => [
        { type: "OrderSummary", id: "DEFAULT_ID" },
      ],
    }),
    addNewOrder: builder.mutation({
      query: (initialOrder) => ({
        url: "/Order",
        method: "POST",
        body: {
          ...initialOrder,
        },
      }),
      invalidatesTags: [
        { type: "Order", id: "LIST" },
        { type: "Notification" },
        { type: "OrderSummary" },
        { type: "IndividualTargetsChart" },
        { type: "SalesChart" },
      ],
    }),
    deleteOrder: builder.mutation({
      query: (orderId) => ({
        url: `/Order/delete/${orderId}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "Order", id: "LIST" },
        { type: "Notification" },
        { type: "OrderSummary" },
        { type: "IndividualTargetsChart" },
        { type: "SalesChart" },
      ],
    }),
  }),
});

export const {
  useGetAllOrdersQuery,
  useGetOrdersByUserIdQuery,
  useGetOrdersByTypeQuery,
  useGetOrdersSummeryByUserIdQuery,
  useAddNewOrderMutation,
  useDeleteOrderMutation,
} = extendedApiSlice;
